import { MATCH_TYPE, OTHER_ID, TOOLTIP_LABELS } from '@/pages/Candidate/view/utils/DecisionTreeDiagram/constants';
import { getOverrideLogicLabelKey } from '@/pages/Candidate/view/utils/DecisionTreeDiagram/utils';
import { NodeData } from '@/pages/Candidate/view/utils/DecisionTreeDiagram/nodes/TooltipNode/types';
import { Maybe } from '@/types/global';
import { FCRAWorkerCheckRecord } from '@/types/turn-api';
import { NodeDataRecordKey } from './types';

/**
 * Removes nullish / falsy entries from an array.
 *
 * Why:
 * - Tooltip `listLabel` is easiest to build as an array of optional strings (some lines only show conditionally).
 * - The UI expects a clean `string[]` with no `null` / `undefined` entries.
 *
 * Note:
 * - This is a "truthy" compact: it will also remove '' and 0 if they appear.
 *   (That’s usually fine for tooltip lines, but keep it in mind.)
 */
const compact = <T>(arr: Array<Maybe<T>>): T[] => arr.filter(Boolean) as T[];

/**
 * Builds the standard tooltip lines for name-based match nodes.
 *
 * Used by:
 * - first_name_match
 * - middle_name_match
 * - last_name_match
 *
 * Output:
 * - ["name_selected: ...", "name_source: ..."] (with any missing values removed)
 */
const nameList = (m?: { name_selected?: string; name_source?: string }) =>
  compact([`name_selected: ${m?.name_selected}`, `name_source: ${m?.name_source}`]);

/**
 * Small constructor for `NodeData`.
 *
 * Why:
 * - Every node object has the same `{ label, value, tooltipProps }` shape.
 * - Centralizes the return type to avoid drift (and keeps `normalizeNodesData` more readable).
 */
const node = (label: string, value: NodeData['value'], tooltipProps: NodeData['tooltipProps']): NodeData => ({
  label,
  tooltipProps,
  value,
});

/**
 * Builds the standard tooltip lines for "second identifier" match nodes where we show:
 * - score
 * - match source (when present)
 * - other_id (only when the match source is OTHER_ID and otherId is available)
 *
 * Used by:
 * - dob_match
 * - ssn_match
 *
 * Output example:
 * - ["score: 0.92", "match_source: OTHER_ID", "other_id: abc123"]
 */
const scoreSourceOtherIdList = (m?: { score?: number; source?: string }, otherId?: string) => {
  const isOther = m?.source === OTHER_ID;
  return compact([
    `score: ${m?.score}`,
    m?.source ? `match_source: ${m.source}` : null,
    otherId && isOther ? `other_id: ${otherId}` : null,
  ]);
};

/**
 * Normalizes an `FCRAWorkerCheckRecord.V6` into the DecisionTreeDiagram node map.
 *
 * Responsibilities:
 * 1) Pull out the relevant nested "match" objects from `surface_json`.
 * 2) Compute derived values that affect labeling (e.g. criminalHit reported vs not reported).
 * 3) Return a consistent `Record<NodeDataRecordKey, NodeData>` used by the diagram renderer.
 *
 * Notes on derived behavior:
 * - criminalHit uses `override_surface_logic` when present; otherwise falls back to `surface_match`.
 * - criminalHit label + tooltip copy depends on whether the computed value is considered a mismatch.
 * - lookback tooltip optionally includes AI response details when present.
 */
export const normalizeNodesData = (data: FCRAWorkerCheckRecord.V6): Record<NodeDataRecordKey, NodeData> | null => {
  if (!data) return null;

  // Top-level primitives / overrides used across multiple nodes.
  const otherId = data?.other_id;
  const override = data?.override_surface_logic;
  const surfaceMatch = data?.surface_match;
  const turnMatch = data?.turn_match;

  // The bulk of the diagram is sourced from `surface_json`.
  const surfaceJson = data?.surface_json;
  const excludeMatch = surfaceJson?.exclude_match;
  const lookbackMatch = surfaceJson?.lookback_match;
  const nameMatch = surfaceJson?.name_match;
  const secondIdMatch = surfaceJson?.second_identifier_match;

  /**
   * criminalHitValue:
   * - If override is explicitly null, we use the underlying `surface_match`.
   * - Otherwise, we respect the override value.
   */
  const criminalHitValue = override === null ? surfaceMatch : override;

  /**
   * criminalHitReported:
   * - Determined by whether criminalHitValue is considered a mismatch.
   * - Drives both the node label and the tooltip label (reported vs notReported).
   */
  const criminalHitReported = !MATCH_TYPE.mismatch.includes(criminalHitValue);

  /**
   * lookback_match tooltip enrichment:
   * - Include AI response details (model/status/etc.) only if present.
   * - Keep them as a list of lines so formatting is consistent and easy to change.
   */
  const ai = lookbackMatch?.ai_response;
  const aiLines = ai
    ? [
        `ai_response.disposition: ${ai?.disposition}`,
        `ai_response.model: ${ai?.model}`,
        `ai_response.status: ${ai?.status}`,
        `ai_response.plea: ${ai?.plea}`,
        `ai_response.prompt: ${ai?.prompt}`,
      ]
    : [];

  // Primary lookback metadata + optional AI metadata, compacted into a final list of lines.
  const lookbackLines = compact([
    lookbackMatch ? `disposition_classification: ${lookbackMatch?.disposition_classification}` : null,
    lookbackMatch ? `classification_source: ${lookbackMatch?.classification_source}` : null,
    lookbackMatch ? `most_restrictive_state: ${lookbackMatch?.most_restrictive_state}` : null,
    ...aiLines,
  ]);

  return {
    /**
     * address_match:
     * - Uses second identifier match data related to address matching.
     * - Tooltip shows selection/source/score so we can debug why it matched (or didn’t).
     */
    address_match: node('AddressMatch', secondIdMatch?.address_match?.value, {
      listLabel: compact([
        `address_selected: ${secondIdMatch?.address_match?.address_selected}`,
        `address_source: ${secondIdMatch?.address_match?.address_source}`,
        `score: ${secondIdMatch?.address_match?.score}`,
      ]),
      position: 'top_left',
    }),

    /**
     * applicant:
     * - Applicant-level lookback configuration (state, strategy, years).
     * - Only shown when lookbackMatch exists.
     */
    applicant: node('Applicant', lookbackMatch?.applicant?.value, {
      listLabel: lookbackMatch
        ? compact([
            `lookback_strategy: ${lookbackMatch?.applicant?.lookback_strategy}`,
            `state: ${lookbackMatch?.applicant?.state}`,
            `years_lookback: ${lookbackMatch?.applicant?.years_lookback}`,
          ])
        : undefined,
      position: 'bottom_left',
    }),

    /**
     * criminalHit:
     * - Derived node: value comes from override/surface logic.
     * - Label and tooltip are chosen based on whether the value represents a mismatch.
     */
    criminalHit: node(`Criminal hit is ${criminalHitReported ? 'reported' : 'not reported'}`, criminalHitValue, {
      label: TOOLTIP_LABELS.criminalHit[criminalHitReported ? 'reported' : 'notReported'],
      position: 'bottom_left',
    }),

    /**
     * dob_match:
     * - Second identifier match for DOB.
     * - Tooltip includes score/source and conditionally other_id when match source is OTHER_ID.
     */
    dob_match: node('DOBMatch', secondIdMatch?.dob_match?.value, {
      listLabel: scoreSourceOtherIdList(secondIdMatch?.dob_match, otherId),
      position: 'top_left',
    }),

    /**
     * exclude_match:
     * - Whether this hit falls within compliance rules (domain-specific).
     * - Tooltip is a short explanation prompt.
     */
    exclude_match: node('ExcludeMatch', excludeMatch?.value, {
      label: 'Is this hit within Turn FCRA compliance rules?',
      position: 'bottom_right',
    }),

    /**
     * first_name_match:
     * - First-name component of name matching.
     * - Tooltip uses the shared nameList helper.
     */
    first_name_match: node('FirstNameMatch', nameMatch?.first_name_match?.value, {
      listLabel: nameList(nameMatch?.first_name_match),
      position: 'top_left',
    }),

    /**
     * hit:
     * - Hit-level lookback configuration (state, strategy, years).
     * - Mirrors the applicant node but for the discovered hit context.
     */
    hit: node('Hit', lookbackMatch?.hit?.value, {
      listLabel: lookbackMatch
        ? compact([
            `lookback_strategy: ${lookbackMatch.hit?.lookback_strategy}`,
            `state: ${lookbackMatch.hit?.state}`,
            `years_lookback: ${lookbackMatch.hit?.years_lookback}`,
          ])
        : undefined,
      position: 'bottom_right',
    }),

    /**
     * last_name_match:
     * - Last-name component of name matching.
     */
    last_name_match: node('LastNameMatch', nameMatch?.last_name_match?.value, {
      listLabel: nameList(nameMatch?.last_name_match),
      position: 'top_left',
    }),

    /**
     * lookback_match:
     * - Core "is this surfaceable" decision output from lookback logic.
     * - Tooltip contains classification metadata and optional AI metadata.
     */
    lookback_match: node('LookbackMatch', lookbackMatch?.value, {
      label: 'Is this record legally surfaceable depending on when and where it happened?',
      listLabel: lookbackLines.length ? [lookbackLines.join('\n')] : undefined,
      position: 'bottom_right',
    }),

    /**
     * middle_name_match:
     * - Middle-name component of name matching.
     */
    middle_name_match: node('MiddleNameMatch', nameMatch?.middle_name_match?.value, {
      listLabel: nameList(nameMatch?.middle_name_match),
      position: 'top_left',
    }),

    /**
     * name_match:
     * - Aggregate name matching node (ties together the name components).
     */
    name_match: node('NameMatch', nameMatch?.value, {
      label: 'Match on the name identifiers discovered in the criminal hit.',
      position: 'bottom_left',
    }),

    /**
     * override_logic:
     * - Exposes the override value used to influence surfacing.
     * - Tooltip label is selected via `getOverrideLogicLabelKey`.
     */
    override_logic: node('Override Logic', override, {
      label: TOOLTIP_LABELS.override_logic[getOverrideLogicLabelKey(override)],
      position: 'bottom_right',
    }),

    /**
     * second_identifier_match:
     * - Aggregate second identifier match node (DOB/SSN/address etc.).
     * - Tooltip includes total_score so we can understand overall strength.
     */
    second_identifier_match: node('SecondIdentifierMatch', secondIdMatch?.value, {
      label: 'A strong second identifier different from the name.',
      listLabel: compact([`total_score: ${secondIdMatch?.total_score}`]),
      position: 'bottom_left',
    }),

    /**
     * ssn_match:
     * - Second identifier match for SSN.
     * - Tooltip includes score/source and conditionally other_id when match source is OTHER_ID.
     */
    ssn_match: node('SSNMatch', secondIdMatch?.ssn_match?.value, {
      listLabel: scoreSourceOtherIdList(secondIdMatch?.ssn_match, otherId),
      position: 'top_left',
    }),

    /**
     * surface_match:
     * - Final surfacing decision output.
     * - Tooltip describes this is the overall decision given all signals.
     */
    surface_match: node('SurfaceMatch', surfaceMatch, {
      label: 'Taking in consideration all available data points and matches, should this record be surfaced.',
      position: 'bottom_left',
    }),

    /**
     * turn_match:
     * - Overall Turn confidence about the hit being a true match vs false positive.
     */
    turn_match: node('TurnMatch', turnMatch, {
      label: 'Level of confidence in the discovered hit corresponding to the applicant and not being false positive.',
      position: 'bottom_left',
    }),
  };
};
